import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../stores';
import { COUNTRIES_BY_REGION } from '../../../../shared/src/constants/countries';
import WechatBindingModal from '../../components/auth/WechatBindingModal';
import { apiService } from '../../../../shared/src/services/api/ApiService';
import { buildStaticUrl } from '../../config/env';

interface ProfileFormData {
  nickname: string;
  email: string;
  phone: string;
  grade: string;
  bio: string;
  country: string;
  province: string;
  city: string;
  language: string;
  gender: number;
}

interface VerificationModal {
  isOpen: boolean;
  type: 'email' | 'phone' | null;
  newValue: string; // 新的联系方式
  verificationCode: string;
  codeSent: boolean;
  countdown: number;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateProfile, sendVerificationCode, verifyAndUpdateContact, getCurrentUser } = useStore();

  // 暂存键名
  const TEMP_STORAGE_KEY = 'profile_temp_data';

  const [formData, setFormData] = useState<ProfileFormData>({
    nickname: '',
    email: '',
    phone: '',
    grade: '',
    bio: '',
    country: '',
    province: '',
    city: '',
    language: '',
    gender: 0,
  });

  const [verificationModal, setVerificationModal] = useState<VerificationModal>({
    isOpen: false,
    type: null,
    newValue: '',
    verificationCode: '',
    codeSent: false,
    countdown: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [wechatBindingModalOpen, setWechatBindingModalOpen] = useState(false);
  const [isUnbinding, setIsUnbinding] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  // 头像缓存破坏版本号，只在头像上传成功后更新
  const [avatarVersion, setAvatarVersion] = useState(() => Date.now());

  // 从localStorage加载暂存数据
  const loadTempData = () => {
    try {
      const tempData = localStorage.getItem(TEMP_STORAGE_KEY);
      if (tempData) {
        const parsed = JSON.parse(tempData);
        console.log('📦 加载暂存的个人资料数据');
        return parsed;
      }
    } catch (error) {
      console.error('加载暂存数据失败:', error);
    }
    return null;
  };

  // 保存数据到localStorage
  const saveTempData = (data: ProfileFormData) => {
    try {
      localStorage.setItem(TEMP_STORAGE_KEY, JSON.stringify(data));
      console.log('💾 个人资料数据已暂存');
    } catch (error) {
      console.error('暂存数据失败:', error);
    }
  };

  // 清除暂存数据
  const clearTempData = () => {
    try {
      localStorage.removeItem(TEMP_STORAGE_KEY);
      console.log('🗑️ 已清除暂存的个人资料数据');
    } catch (error) {
      console.error('清除暂存数据失败:', error);
    }
  };

  // 初始化用户数据
  useEffect(() => {
    // 获取最新的用户信息
    getCurrentUser().catch(console.error);
  }, [getCurrentUser]);

  useEffect(() => {
    if (user) {
      // 先尝试加载暂存数据
      const tempData = loadTempData();
      
      if (tempData) {
        // 如果有暂存数据，使用暂存数据但保留联系方式为服务器数据
        setFormData({
          ...tempData,
          email: user.email || tempData.email || '',
          phone: user.phone || tempData.phone || '',
        });
      } else {
        // 没有暂存数据，使用服务器数据
        setFormData({
          nickname: user.nickname || '',
          email: user.email || '',
          phone: user.phone || '',
          grade: user.grade || '',
          bio: user.bio || '',
          country: user.country || '',
          province: user.province || '',
          city: user.city || '',
          language: user.language || 'zh_CN',
          gender: user.gender || 0,
        });
      }
    }
  }, [user]);

  // 倒计时效果
  useEffect(() => {
    const timer = setInterval(() => {
      setVerificationModal(prev => ({
        ...prev,
        countdown: prev.countdown > 0 ? prev.countdown - 1 : 0,
      }));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleInputChange = (field: keyof ProfileFormData, value: string | number) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    
    // 实时保存到localStorage（除了联系方式，联系方式通过验证流程更新）
    if (field !== 'email' && field !== 'phone') {
      saveTempData(newData);
    }
    
    setError('');
    setSuccess('');
  };

  // 处理头像上传
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('不支持的文件格式，请上传 JPG、PNG、GIF 或 WebP 格式的图片');
      return;
    }

    // 验证文件大小 (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('文件大小超过 10MB 限制，请选择更小的图片');
      return;
    }

    try {
      setIsUploadingAvatar(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('avatar', file);

      // 使用 apiService.post，但需要设置正确的 Content-Type
      const response = await apiService.post('/user/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.success) {
        setSuccess('头像上传成功');
        setAvatarVersion(Date.now());
        // 刷新用户信息
        await getCurrentUser();
      } else {
        throw new Error(response.error?.message || '头像上传失败');
      }
    } catch (err: any) {
      setError(err.message || '头像上传失败');
    } finally {
      setIsUploadingAvatar(false);
      // 清空 input，允许重新选择同一文件
      e.target.value = '';
    }
  };



  // 开始修改联系方式
  const startEditContact = (type: 'email' | 'phone') => {
    // 在开始验证前，保存当前表单数据
    saveTempData(formData);
    
    setVerificationModal({
      isOpen: true,
      type,
      newValue: '',
      verificationCode: '',
      codeSent: false,
      countdown: 0,
    });
    setError('');
    setSuccess('');
  };

  // 关闭修改弹窗
  const closeModal = () => {
    setVerificationModal({
      isOpen: false,
      type: null,
      newValue: '',
      verificationCode: '',
      codeSent: false,
      countdown: 0,
    });
    setError('');
    setSuccess('');
  };

  // 发送验证码到新联系方式
  const handleSendVerificationCode = async () => {
    if (!verificationModal.newValue) {
      setError('请输入新的' + (verificationModal.type === 'email' ? '邮箱' : '手机号'));
      return;
    }

    // 验证格式
    if (verificationModal.type === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(verificationModal.newValue)) {
        setError('请输入有效的邮箱地址');
        return;
      }
    } else {
      const phoneRegex = /^1[3-9]\d{9}$/;
      if (!phoneRegex.test(verificationModal.newValue)) {
        setError('请输入有效的手机号');
        return;
      }
    }

    try {
      setIsLoading(true);
      setError('');

      const success = await sendVerificationCode({
        [verificationModal.type!]: verificationModal.newValue,
        type: 'profile_verify',
      });

      if (success) {
        setVerificationModal(prev => ({
          ...prev,
          codeSent: true,
          countdown: 60,
        }));
        setSuccess('验证码已发送到新' + (verificationModal.type === 'email' ? '邮箱' : '手机号'));
      } else {
        setError('验证码发送失败，请稍后重试');
      }
    } catch (err: any) {
      setError(err.message || '发送验证码失败');
    } finally {
      setIsLoading(false);
    }
  };

  // 验证新联系方式并完成修改
  const handleVerifyAndUpdate = async () => {
    if (!verificationModal.newValue || !verificationModal.verificationCode) {
      setError('请输入验证码');
      return;
    }

    if (verificationModal.verificationCode.length !== 6) {
      setError('验证码必须是6位数字');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const success = await verifyAndUpdateContact({
        [verificationModal.type!]: verificationModal.newValue,
        verificationCode: verificationModal.verificationCode,
        type: 'profile_verify',
      });

      if (success) {
        // 更新本地用户信息
        const newFormData = { ...formData };
        if (verificationModal.type === 'email') {
          newFormData.email = verificationModal.newValue;
        } else {
          newFormData.phone = verificationModal.newValue;
        }
        setFormData(newFormData);
        
        // 更新暂存数据
        saveTempData(newFormData);

        closeModal();
        setSuccess(verificationModal.type === 'email' ? '邮箱修改成功' : '手机号修改成功');
      } else {
        setError('验证失败，请检查验证码是否正确');
      }
    } catch (err: any) {
      setError(err.message || '验证失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      setError('');

      // 只更新基本信息，联系方式通过验证流程单独更新
      const updateData = {
        nickname: formData.nickname,
        grade: formData.grade,
        bio: formData.bio,
        country: formData.country,
        province: formData.province,
        city: formData.city,
        language: formData.language,
        gender: formData.gender,
      };

      await updateProfile(updateData);
      
      // 更新成功后清除暂存数据
      clearTempData();
      setSuccess('个人信息更新成功');
    } catch (err: any) {
      setError(err.message || '更新失败');
    } finally {
      setIsLoading(false);
    }
  };

  const gradeOptions = [
    '小学1年级', '小学2年级', '小学3年级', '小学4年级', '小学5年级', '小学6年级',
    '初中1年级', '初中2年级', '初中3年级',
    '高中1年级', '高中2年级', '高中3年级',
    '大学', '其他'
  ];

  // 打开微信关联弹窗
  const handleOpenWechatBinding = () => {
    setWechatBindingModalOpen(true);
    setError('');
    setSuccess('');
  };

  // 关闭微信关联弹窗
  const handleCloseWechatBinding = () => {
    setWechatBindingModalOpen(false);
  };

  // 微信关联成功
  const handleWechatBindingSuccess = async () => {
    console.log('=== Wechat binding success callback ===');
    console.log('Current user before refresh:', user?.wechat_openid ? 'has openid' : 'no openid');
    
    // 刷新用户信息
    try {
      await getCurrentUser();
      console.log('getCurrentUser completed');
      
      // 延迟一下显示成功消息，确保状态已更新
      setTimeout(() => {
        setSuccess('微信账号关联成功！');
      }, 100);
    } catch (error) {
      console.error('Failed to refresh user after binding:', error);
      setError('关联成功，但刷新用户信息失败，请刷新页面');
    }
  };

  // 解除微信关联
  const handleUnbindWechat = async () => {
    if (!confirm('确定要解除微信关联吗？解除后将无法使用微信快速登录。')) {
      return;
    }

    try {
      setIsUnbinding(true);
      setError('');

      const response = await apiService.post('/auth/wechat/binding/unbind');

      if (response.success) {
        setSuccess('已解除微信关联');
        // 刷新用户信息
        await getCurrentUser();
      } else {
        throw new Error(response.error?.message || '解除关联失败');
      }
    } catch (err: any) {
      setError(err.message || '解除关联失败');
    } finally {
      setIsUnbinding(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* 头部 */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/chat')}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">个人资料</h1>
          </div>
        </div>

        {/* 暂存数据提示 */}
        {loadTempData() && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <p className="text-blue-600 dark:text-blue-400">检测到未保存的修改，已自动恢复您之前的输入</p>
              </div>
              <button
                onClick={clearTempData}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-sm underline"
              >
                清除暂存
              </button>
            </div>
          </div>
        )}

        {/* 错误和成功提示 */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-green-600 dark:text-green-400">{success}</p>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* 头像和基本信息区域 */}
            <div className="flex items-start space-x-6">
              <div className="relative group">
                {user?.avatar ? (
                  <img
                    src={`${buildStaticUrl(user.avatar)}?v=${avatarVersion}`}
                    alt="头像"
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-2xl font-semibold">
                    {formData.nickname?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
                {isUploadingAvatar && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => document.getElementById('avatar-upload')?.click()}
                  disabled={isUploadingAvatar}
                  className="absolute bottom-0 right-0 w-7 h-7 bg-white dark:bg-gray-700 rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors border-2 border-white dark:border-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="更换头像"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleAvatarUpload}
                  className="hidden"
                  disabled={isUploadingAvatar}
                />
              </div>
              <div className="flex-1">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">头像</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">点击笔形图标更换头像，支持 JPG、PNG、GIF、WebP 格式</p>
                </div>
                
                {/* 基本信息快速编辑 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* 性别 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      性别
                    </label>
                    <select
                      value={formData.gender}
                      onChange={(e) => handleInputChange('gender', parseInt(e.target.value))}
                      className="input text-sm"
                    >
                      <option value={0}>未设置</option>
                      <option value={1}>男</option>
                      <option value={2}>女</option>
                    </select>
                  </div>

                  {/* 语言 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      语言
                    </label>
                    <select
                      value={formData.language}
                      onChange={(e) => handleInputChange('language', e.target.value)}
                      className="input text-sm"
                    >
                      <option value="zh_CN">简体中文</option>
                      <option value="zh_TW">繁体中文</option>
                      <option value="en_US">English</option>
                    </select>
                  </div>

                  {/* 国家/地区 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      国家/地区
                    </label>
                    <select
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      className="input text-sm"
                    >
                      <option value="">请选择</option>
                      
                      {/* 中国地区 */}
                      <optgroup label="中国地区">
                        {COUNTRIES_BY_REGION.chinese.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </optgroup>
                      
                      {/* 主要英语国家 */}
                      <optgroup label="英语国家">
                        {COUNTRIES_BY_REGION.english.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </optgroup>
                      
                      {/* 主要西班牙语国家 */}
                      <optgroup label="西班牙语国家">
                        {COUNTRIES_BY_REGION.spanish.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </optgroup>
                      
                      {/* 其他国家 */}
                      <optgroup label="其他国家">
                        {COUNTRIES_BY_REGION.others.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  {/* 省份 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      省份
                    </label>
                    <input
                      type="text"
                      value={formData.province}
                      onChange={(e) => handleInputChange('province', e.target.value)}
                      className="input text-sm"
                      placeholder="请输入省份"
                    />
                  </div>

                  {/* 城市 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      城市
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="input text-sm"
                      placeholder="请输入城市"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 基本信息 */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                基本信息
              </h3>

              {/* 昵称 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  昵称 *
                </label>
                <input
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => handleInputChange('nickname', e.target.value)}
                  className="input"
                  placeholder="请输入昵称"
                  required
                />
              </div>

              {/* 年级 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  年级
                </label>
                <select
                  value={formData.grade}
                  onChange={(e) => handleInputChange('grade', e.target.value)}
                  className="input"
                >
                  <option value="">请选择年级</option>
                  {gradeOptions.map(grade => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>

              {/* 个人简介 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  个人简介
                </label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  className="input min-h-[100px] resize-none"
                  placeholder="介绍一下自己吧..."
                  maxLength={200}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.bio.length}/200
                </p>
              </div>
            </div>

            {/* 联系方式 */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                联系方式
              </h3>

              {/* 邮箱 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  邮箱
                  {user?.email_verified && (
                    <span className="ml-2 inline-flex items-center text-xs text-green-600 dark:text-green-400">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      已验证
                    </span>
                  )}
                </label>

                <div className="flex items-center space-x-3">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onClick={() => !formData.email && startEditContact('email')}
                    className={`input flex-1 ${!formData.email ? 'cursor-pointer hover:border-primary-300 hover:bg-primary-50' : 'cursor-default'}`}
                    placeholder={formData.email ? "" : "点击添加邮箱"}
                    disabled={!!formData.email} // 如果已有邮箱则锁定
                    readOnly={!!formData.email}
                  />
                  {formData.email ? (
                    <button
                      type="button"
                      onClick={() => startEditContact('email')}
                      className="btn-secondary text-sm"
                    >
                      修改
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEditContact('email')}
                      className="px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors"
                      style={{ backgroundColor: '#07c160' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#06ad56')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
                    >
                      添加
                    </button>
                  )}
                </div>
              </div>

              {/* 手机号 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  手机号
                  {user?.phone_verified && (
                    <span className="ml-2 inline-flex items-center text-xs text-green-600 dark:text-green-400">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      已验证
                    </span>
                  )}
                </label>

                <div className="flex items-center space-x-3">
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    onClick={() => !formData.phone && startEditContact('phone')}
                    className={`input flex-1 ${!formData.phone ? 'cursor-pointer hover:border-primary-300 hover:bg-primary-50' : 'cursor-default'}`}
                    placeholder={formData.phone ? "" : "点击添加手机号"}
                    disabled={!!formData.phone} // 如果已有手机号则锁定
                    readOnly={!!formData.phone}
                  />
                  {formData.phone ? (
                    <button
                      type="button"
                      onClick={() => startEditContact('phone')}
                      className="btn-secondary text-sm"
                    >
                      修改
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEditContact('phone')}
                      className="px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors"
                      style={{ backgroundColor: '#07c160' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#06ad56')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
                    >
                      添加
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 第三方账号 */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                第三方账号
              </h3>

              <div className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                user?.wechat_openid 
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                  : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    user?.wechat_openid 
                      ? 'bg-green-500' 
                      : 'bg-gray-400 dark:bg-gray-600'
                  }`}>
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.5 9.51a4.22 4.22 0 01-1.91-1.34A5.77 5.77 0 0015 6a4.72 4.72 0 00-5.5-.48 4.78 4.78 0 00-2.54 3.79 7.5 7.5 0 00.1 2.5H6a2 2 0 00-2 2v7a2 2 0 002 2h12a2 2 0 002-2v-7a2 2 0 00-2-2h-1.06a7.5 7.5 0 00.1-2.5 4.78 4.78 0 00-2.54-3.79z" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-gray-900 dark:text-white">微信</p>
                      {user?.wechat_openid ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          已关联
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          未关联
                        </span>
                      )}
                    </div>
                    {user?.wechat_openid ? (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        可使用微信小程序扫码登录电脑端
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        关联后可使用微信小程序扫码登录
                      </p>
                    )}
                  </div>
                </div>
                {user?.wechat_openid ? (
                  <button
                    type="button"
                    onClick={handleUnbindWechat}
                    disabled={isUnbinding}
                    className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUnbinding ? '解除中...' : '解除关联'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleOpenWechatBinding}
                    className="px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors"
                    style={{ backgroundColor: '#07c160' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#06ad56')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
                  >
                    关联微信
                  </button>
                )}
              </div>

              {/* 微信关联说明 */}
              {!user?.wechat_openid && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-blue-800 dark:text-blue-300">
                      <p className="font-medium mb-1">关联微信后可以：</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>使用微信扫码快速登录</li>
                        <li>在小程序中使用完整功能</li>
                        <li>多端数据同步</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>


            {/* 提交按钮 */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => navigate('/chat')}
                className="btn-secondary"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#07c160' }}
                onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#06ad56')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
              >
                {isLoading ? '保存中...' : '保存'}
              </button>
            </div>
          </form>
        </div>

        {/* 微信关联弹窗 */}
        <WechatBindingModal
          isOpen={wechatBindingModalOpen}
          onClose={handleCloseWechatBinding}
          onSuccess={handleWechatBindingSuccess}
        />

        {/* 验证弹窗 */}
        {verificationModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    修改{verificationModal.type === 'email' ? '邮箱' : '手机号'}
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  {/* 新联系方式输入 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      新{verificationModal.type === 'email' ? '邮箱' : '手机号'}
                    </label>
                    <input
                      type={verificationModal.type === 'email' ? 'email' : 'tel'}
                      value={verificationModal.newValue}
                      onChange={(e) => setVerificationModal(prev => ({ ...prev, newValue: e.target.value }))}
                      className="input w-full"
                      placeholder={`请输入新${verificationModal.type === 'email' ? '邮箱' : '手机号'}`}
                    />
                  </div>

                  {/* 发送验证码 */}
                  <div>
                    <button
                      type="button"
                      onClick={handleSendVerificationCode}
                      disabled={verificationModal.countdown > 0 || isLoading || !verificationModal.newValue}
                      className="w-full px-4 py-2 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                      style={{ backgroundColor: '#07c160' }}
                      onMouseEnter={(e) => !(verificationModal.countdown > 0 || isLoading || !verificationModal.newValue) && (e.currentTarget.style.backgroundColor = '#06ad56')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
                    >
                      {verificationModal.countdown > 0
                        ? `${verificationModal.countdown}s后重新发送`
                        : '发送验证码'
                      }
                    </button>
                  </div>

                  {/* 验证码输入 */}
                  {verificationModal.codeSent && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        验证码
                      </label>
                      <input
                        type="text"
                        value={verificationModal.verificationCode}
                        onChange={(e) => setVerificationModal(prev => ({ ...prev, verificationCode: e.target.value }))}
                        className="input w-full"
                        placeholder="请输入6位验证码"
                        maxLength={6}
                      />
                    </div>
                  )}

                  {/* 操作按钮 */}
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="btn-secondary flex-1"
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      onClick={handleVerifyAndUpdate}
                      disabled={!verificationModal.codeSent || !verificationModal.verificationCode || isLoading}
                      className="flex-1 px-4 py-2 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                      style={{ backgroundColor: '#07c160' }}
                      onMouseEnter={(e) => !(!verificationModal.codeSent || !verificationModal.verificationCode || isLoading) && (e.currentTarget.style.backgroundColor = '#06ad56')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#07c160')}
                    >
                      {isLoading ? '验证中...' : '完成修改'}
                    </button>
                  </div>


                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;